import { theme, useResponsive } from "@product/scmp-sdk";
import Head from "next/head";
import { useRouter } from "next/router";
import React, { useMemo } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import type { RelayComponentWithLayout } from "relay-nextjs";
import { withRelay } from "relay-nextjs";

import { FallbackLoading } from "~/components/fallback-loading";
import { getUniqueArticlesPerSection } from "~/components/magazines-style";
import type { SubSection } from "~/components/magazines-style/consts";
import { SectionNameMap } from "~/components/magazines-style/consts";
import { ViewportTag } from "~/components/meta";
import { StoryStyleContextProvider } from "~/components/story-style/contexts";
import { createClientEnvironment } from "~/lib/relay/environment.client";
import type { storyStylePageQuery } from "~/queries/__generated__/storyStylePageQuery.graphql";

import { StyledStoryStyleLargeLayout, StyledStoryStyleLayout } from "./styles";

const pageQuery = graphql`
  query storyStylePageQuery {
    ...helpersStyleArticleListQuery
  }
`;

const StoryStylePage: RelayComponentWithLayout<unknown, storyStylePageQuery> = ({
  preloadedQuery,
}) => {
  const preloadedData = usePreloadedQuery(pageQuery, preloadedQuery);
  const router = useRouter();
  const uniqueArticlesPerSection = useMemo(() => {
    const routerSubSection = router.query.subSection as SubSection;
    return getUniqueArticlesPerSection({
      currentSectionEntityId: SectionNameMap[routerSubSection].entityId,
      reference: preloadedData,
    });
  }, [preloadedData, router.query.subSection]);

  const totalArticleCount = useMemo(
    () => uniqueArticlesPerSection.selectedEntityIds.length,
    [uniqueArticlesPerSection],
  );

  const isMediumDesktopUp = useResponsive(theme.breakpoints.up("mediumDesktop"), false);
  const isMediumDesktopDown = useResponsive(theme.breakpoints.down("mediumDesktop"), false);

  if (!preloadedData) return null;

  return (
    <>
      {isMediumDesktopUp && (
        <StoryStyleContextProvider totalArticleCount={totalArticleCount}>
          <StyledStoryStyleLargeLayout
            layoutBreakpoint={theme.breakpoints.up("mediumDesktop")}
            reference={uniqueArticlesPerSection.selectedEdges}
            uniqueArticlesPerSection={uniqueArticlesPerSection.data}
          />
        </StoryStyleContextProvider>
      )}
      {isMediumDesktopDown && (
        <StoryStyleContextProvider totalArticleCount={totalArticleCount} hasAdCards>
          <StyledStoryStyleLayout
            layoutBreakpoint={theme.breakpoints.down("mediumDesktop")}
            uniqueArticlesPerSection={uniqueArticlesPerSection.data}
          />
        </StoryStyleContextProvider>
      )}
    </>
  );
};

StoryStylePage.displayName = "StoryStylePage";

const RelayConnectedStoryStylePage: RelayComponentWithLayout<unknown, storyStylePageQuery> =
  withRelay(StoryStylePage, pageQuery, {
    createClientEnvironment: () => createClientEnvironment(),
    createServerEnvironment: async context => {
      const { createServerEnvironment } = await import("~/lib/relay/environment.server");
      return createServerEnvironment(context.req?.cookies);
    },
    fallback: <FallbackLoading />,
  });

RelayConnectedStoryStylePage.getLayout = function getLayout(page) {
  return (
    <>
      <Head>
        <title>STYLE Stories | South China Morning Post</title>
        <meta content={ViewportTag.content} name={ViewportTag.name} />
      </Head>
      {page}
    </>
  );
};

RelayConnectedStoryStylePage.displayName = "RelayConnectedStoryStylePage";

export default RelayConnectedStoryStylePage;
